import type { EventOccurrence, EventType, Person } from "@shared/types";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { formatDateTimeRange } from "@/utils/date";
import { describeRRule } from "@shared/recurrence";
import { appleMapsUrl, googleMapsUrl, isApplePlatform } from "@/utils/mapLink";

interface EventDetailDrawerProps {
  occurrence: EventOccurrence | null;
  people: Person[];
  eventTypes: EventType[];
  isAdmin: boolean;
  onClose: () => void;
  onEdit: (occurrence: EventOccurrence) => void;
  onDuplicate: (occurrence: EventOccurrence) => void;
  onDelete: (occurrence: EventOccurrence) => void;
}

export function EventDetailDrawer({
  occurrence,
  people,
  eventTypes,
  isAdmin,
  onClose,
  onEdit,
  onDuplicate,
  onDelete,
}: EventDetailDrawerProps) {
  if (!occurrence) return null;

  const eventType = eventTypes.find((t) => t.id === occurrence.eventTypeId);
  const participants = occurrence.participantIds
    .map((id) => people.find((p) => p.id === id))
    .filter((p): p is Person => Boolean(p));

  const mapQuery = occurrence.address || occurrence.locationName;
  const appleUrl = mapQuery ? appleMapsUrl(mapQuery) : null;
  const googleUrl = mapQuery ? googleMapsUrl(mapQuery) : null;
  const preferApple = isApplePlatform();

  return (
    <Modal open={Boolean(occurrence)} onClose={onClose} align="right">
      <div className="flex items-center justify-between border-b border-line dark:border-line-dark px-5 py-4">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-white"
          style={{ backgroundColor: eventType?.color ?? "#6B7280" }}
        >
          {eventType?.name ?? "Event"}
        </span>
        <button
          onClick={onClose}
          aria-label="Close"
          className="rounded-full p-1.5 text-muted hover:bg-black/5 dark:hover:bg-white/10"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5">
        <h2 className="text-xl font-semibold leading-tight text-ink dark:text-ink-dark">{occurrence.title}</h2>
        {occurrence.opponent && (
          <p className="mt-1 text-sm text-muted dark:text-muted-dark">vs {occurrence.opponent}</p>
        )}

        <p className="mt-3 text-sm font-medium text-ink dark:text-ink-dark">
          {formatDateTimeRange(occurrence.occurrenceStart, occurrence.endAt, occurrence.allDay, occurrence.timezone)}
        </p>
        {occurrence.rrule && (
          <p className="text-xs text-muted dark:text-muted-dark">
            {describeRRule(occurrence.rrule, occurrence.recurrenceEndAt)}
            {occurrence.isException ? " · edited for this date" : ""}
          </p>
        )}

        {participants.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {participants.map((p) => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white"
                style={{ backgroundColor: p.color }}
              >
                {p.name}
              </span>
            ))}
          </div>
        )}

        {occurrence.team && (
          <div className="mt-4 text-sm">
            <span className="text-muted dark:text-muted-dark">Team / organization</span>
            <p className="font-medium">{occurrence.team}</p>
          </div>
        )}

        {occurrence.locationName && (
          <div className="mt-4 text-sm">
            <span className="text-muted dark:text-muted-dark">Location</span>
            <p className="font-medium">{occurrence.locationName}</p>
            {occurrence.address && <p className="text-muted dark:text-muted-dark">{occurrence.address}</p>}
            {mapQuery && (
              <div className="mt-2 flex gap-2">
                <a
                  href={preferApple ? appleUrl! : googleUrl!}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-3 py-1.5 text-xs font-semibold text-white dark:bg-white dark:text-ink"
                >
                  Get directions
                </a>
                <a
                  href={preferApple ? googleUrl! : appleUrl!}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-line dark:border-line-dark px-3 py-1.5 text-xs font-medium text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
                >
                  {preferApple ? "Open in Google Maps" : "Open in Apple Maps"}
                </a>
              </div>
            )}
          </div>
        )}

        {occurrence.notes && (
          <div className="mt-4 text-sm">
            <span className="text-muted dark:text-muted-dark">Notes</span>
            <p className="whitespace-pre-line font-medium">{occurrence.notes}</p>
          </div>
        )}

        {occurrence.reminderNote && (
          <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">
            <span className="font-semibold">Reminder: </span>
            {occurrence.reminderNote}
          </div>
        )}

        {occurrence.sourceUrl && (
          <div className="mt-4 text-sm">
            <a
              href={occurrence.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-accent hover:underline"
            >
              View original schedule ↗
            </a>
          </div>
        )}
      </div>

      {isAdmin && (
        <div className="flex items-center justify-between gap-2 border-t border-line dark:border-line-dark px-5 py-4">
          <Button variant="danger" size="sm" onClick={() => onDelete(occurrence)}>
            Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => onDuplicate(occurrence)}>
              Duplicate
            </Button>
            <Button variant="primary" size="sm" onClick={() => onEdit(occurrence)}>
              Edit
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
